import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import ClearIcon from "@mui/icons-material/Clear";

const EditWorkItemsDetails = ({ tender, data, setData, setStep, user }) => {
  const [units, setUnits] = useState();
  const fileInputRef = useRef(null);
  const [itemLoading, setItemLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorModal, setErrorModal] = useState(false);

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  useEffect(() => {
    const getWorkItems = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tender/work-items?tenderId=${tender.tenderId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } }
        );
        if (res.data.length > 0) {
          setData(res.data);
        }
        setItemLoading(false);
      } catch (error) {
        console.log(error);
      }
    };
    getWorkItems();
  }, []);

  useEffect(() => {
    const getUnits = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/unit`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } }
        );
        setUnits(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getUnits();
  }, []);

  const groupedUnits = units?.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const unitOptions = groupedUnits
    ? Object.entries(groupedUnits).flatMap(([category, units]) =>
        units.map((unit) => ({
          ...unit,
          category,
        }))
      )
    : [];

  const handleChange = (index, field, value) => {
    if (value.includes(".")) {
      const [intPart, decPart] = value.split(".");
      if (decPart.length > 2) {
        value = intPart + "." + decPart.slice(0, 2);
      }
    }
    const updatedData = [...data];
    updatedData[index][field] = value;
    updatedData[index].total =
      updatedData[index].quantity * updatedData[index].rate;
    setData(updatedData);
  };

  const addRow = () => {
    setData([
      ...data,
      {
        itemName: "",
        description: "",
        section: "",
        unit: "",
        quantity: 0,
        rate: 0,
        total: 0,
      },
    ]);
  };

  const deleteRow = (index) => {
    const updatedData = data.filter((_, i) => i !== index);
    setData(updatedData);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Work Items");
    XLSX.writeFile(workbook, "WorkItemsDetails.xlsx");
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const bufferArray = e.target.result;
      const workbook = XLSX.read(bufferArray, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet);

      const validUnitNames = new Set(
        units.map((unit) => unit.shortform?.toLowerCase())
      );

      let hasInvalidUnit = false;

      const updatedData = parsedData.map((row) => {
        const unitValue = row.unit?.toString().trim().toLowerCase();
        const isValidUnit = validUnitNames.has(unitValue);

        if (!isValidUnit) {
          hasInvalidUnit = true;
          row.unit = ""; // clear invalid unit
        }
        const quantity = row.quantity ? parseFloat(row.quantity) : 0;
        const rate = row.rate ? parseFloat(row.rate) : 0;

        return {
          ...row,
          itemName: row.itemName ? String(row.itemName).trim() : "",
          quantity: quantity.toFixed(2),
          rate: rate.toFixed(2),
          total: (quantity * rate).toFixed(2), // ← total always valid
        };
      });
      setData(updatedData);
      setIsImporting(false);
      event.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const grandTotal = data?.reduce((sum, row) => sum + row.total, 0);
  const validate = (data) => {
    const newFieldErrors = {};

    data?.forEach((item, index) => {
      const isAllEmpty =
        (!item.itemName || item.itemName.toString().trim() === "") &&
        (!item.description || item.description.toString().trim() === "") &&
        (!item.section || item.section.toString().trim() === "") &&
        (!item.unit || item.unit.toString().trim() === "") &&
        (!item.quantity || Number(item.quantity) <= 0);

      if (isAllEmpty) return;

      const errors = {};
      if (!item.itemName?.trim()) errors.itemName = true;
      if (!item.description?.trim()) errors.description = true;
      if (!item.section?.trim()) errors.section = true;
      if (!item.unit?.trim()) errors.unit = true;
      if (!item.quantity || Number(item.quantity) <= 0) errors.quantity = true;

      if (Object.keys(errors).length > 0) {
        newFieldErrors[index] = errors;
      }
    });

    setFieldErrors(newFieldErrors);
    return Object.keys(newFieldErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validate(data)) {
      return;
    }

    const filteredData = data.filter((item) => {
      const isAllEmpty =
        (!item.itemName || item.itemName.toString().trim() === "") &&
        (!item.description || item.description.toString().trim() === "") &&
        (!item.section || item.section.toString().trim() === "") &&
        (!item.unit || item.unit.toString().trim() === "") &&
        (!item.quantity || Number(item.quantity) <= 0);

      if (isAllEmpty) {
        return false; // drop empty rows
      }

      const isAllFilled =
        item.itemName?.toString().trim() !== "" &&
        item.description?.toString().trim() !== "" &&
        item.section?.toString().trim() !== "" &&
        item.unit?.toString().trim() !== "" &&
        item.quantity &&
        Number(item.quantity) > 0;

      return isAllFilled; // only keep fully filled rows
    });

    if (!validate(filteredData)) {
      return;
    }
    if (filteredData.length === 0) {
      setErrorModal(true);
      return;
    }
    setData(filteredData);
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  return (
    <div>
      {isImporting && (
        <div className="loading-div uploading-div">
          Uploading Sheet... <CircularProgress color="#fff" />
        </div>
      )}
      <h2>Work Items Details</h2>
      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            maxWidth: "100%",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Description</th>
              <th>Section</th>
              <th>Unit</th>
              <th>Quantity</th>
              <th>Rate</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {itemLoading ? (
              <Box>Loading...</Box>
            ) : (
              data
                ?.sort((a, b) => a.itemOrder - b.itemOrder)
                .map((row, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        className={`table-input ${
                          fieldErrors[index]?.itemName && "input-error"
                        }`}
                        type="text"
                        value={row.itemName}
                        onChange={(e) =>
                          handleChange(index, "itemName", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input ${
                          fieldErrors[index]?.description && "input-error"
                        }`}
                        type="text"
                        value={row.description}
                        onChange={(e) =>
                          handleChange(index, "description", e.target.value)
                        }
                        maxLength={2000}
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input ${
                          fieldErrors[index]?.section && "input-error"
                        }`}
                        type="text"
                        value={row.section}
                        onChange={(e) =>
                          handleChange(index, "section", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Autocomplete
                        className={`unit-dropdown ${
                          fieldErrors[index]?.unit && "input-error"
                        }`}
                        options={unitOptions}
                        getOptionLabel={(option) =>
                          `${option.title} (${option.shortform})`
                        }
                        groupBy={(option) => option.category}
                        value={
                          unitOptions.find(
                            (opt) => opt.shortform === row.unit
                          ) || null
                        }
                        onChange={(event, newValue) =>
                          handleChange(
                            index,
                            "unit",
                            newValue ? newValue.shortform : ""
                          )
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.shortform === value.shortform
                        }
                        disablePortal
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Choose Unit"
                            variant="outlined"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                minHeight: "45px",
                                fontSize: "12px",
                              },
                              "& input": {
                                padding: "15px",
                              },
                            }}
                          />
                        )}
                        filterSelectedOptions
                        autoHighlight
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disableClearable
                        sx={{ width: "170px" }}
                        slotProps={{
                          paper: {
                            sx: {
                              background: "#D8E9F0",
                              "& .MuiAutocomplete-option": {
                                fontSize: "12px",
                                padding: "6px 12px",
                              },
                              "& .MuiAutocomplete-groupLabel": {
                                fontWeight: "bold",
                                color: "#000",
                                fontSize: "13px",
                                backgroundColor: "#D8E9F0",
                                padding: "0px 6px",
                                lineHeight: "2",
                                position: "static",
                              },
                            },
                          },
                        }}
                      />
                    </td>
                    <td>
                      <input
                        className={`table-input ${
                          fieldErrors[index]?.quantity && "input-error"
                        }`}
                        type="number"
                        value={row.quantity}
                        style={{ width: "80px" }}
                        onChange={(e) =>
                          handleChange(index, "quantity", e.target.value)
                        }
                        step={0.01}
                      />
                    </td>
                    <td>
                      <input
                        className="table-input"
                        type="number"
                        value={row.rate !== null ? row.rate : 0}
                        onChange={(e) =>
                          handleChange(index, "rate", e.target.value)
                        }
                        step={0.01}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {Number(row.total || 0).toFixed(2)}
                    </td>
                    <td>
                      {/* <Tooltip title="Remove Row"> */}
                      <Button
                        className="btn-contained"
                        onClick={() => deleteRow(index)}
                        sx={{
                          margin: "0 !important",
                          borderRadius: "5px !important",
                          minWidth: "50px !important",
                          width: "0",
                          "&:hover": { background: "#EB2B0B !important" },
                        }}
                      >
                        <ClearIcon />
                      </Button>
                      {/* </Tooltip> */}
                    </td>
                  </tr>
                ))
            )}

            <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
              <td
                colSpan={6}
                style={{ textAlign: "right", paddingRight: "10px" }}
              >
                Grand Total:
              </td>
              <td style={{ width: "150px", textAlign: "center" }}>
                {grandTotal}
              </td>
            </tr>
          </tbody>
        </table>
        <Typography variant="p" fontWeight={700} fontSize={"14px"}>
          [Important Note: Uploading Sheet will overwrite existing work items.]
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Button className="btn-outlined" onClick={handleFileSelect}>
            Upload Excel
          </Button>
          <Button
            className="btn-outlined"
            onClick={addRow}
            sx={{ padding: "5px 40px !important" }}
          >
            Add Work Item
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <button
            onClick={exportToExcel}
            style={{
              cursor: "pointer",
              border: "none",
              background: "none",
              borderBottom: "1px solid #000",
            }}
          >
            Download Template Excel
          </button>
        </div>
      </div>
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={importFromExcel}
        style={{ display: "none" }}
      />
      <div style={{ display: "flex", justifyContent: "end" }}>
        <div className="btn-container" style={{ gap: 20, margin: "0" }}>
          <Button className="btn-outlined" onClick={handleBack}>
            Back
          </Button>
          <Button className="btn-contained" onClick={handleNext}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditWorkItemsDetails;
